import { useCallback, useRef, useEffect } from 'react';
import { DEFAULT_FONT_SIZE } from '../../items/text/create-text-item';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { editAndRelayoutText } from '../../state/actions/edit-and-relayout-text';
import { relayoutCaptions } from '../../state/actions/relayout-captions';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { FormatSize } from '@crabs-ai/web-icons';
import { ValueInputField } from '@crabs-ai/ds-react';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { useTextSelection } from '../../contexts/text-selection-context';

/**
 * TextItem에서 fontSize 값을 추출 (segments 고려)
 */
const getFontSizeFromTextItem = (item: TextItem | CaptionsItem): number => {
  if (item.type !== 'text') {
    return item.fontSize;
  }

  const textItem = item as TextItem;
  if (!textItem.segments || textItem.segments.length === 0) {
    return textItem.fontSize;
  }

  // segments의 fontSize를 추출 (없으면 item.fontSize 사용)
  const fontSizes = textItem.segments.map(
    (s) => s.fontSize ?? textItem.fontSize,
  );

  // 모든 fontSize가 같은지 확인
  const firstSize = fontSizes[0]!;
  const allSame = fontSizes.every((f) => f === firstSize);
  if (allSame) {
    return firstSize;
  }

  // Mixed 상태 - 특수 값 반환 (실제로는 -1을 Mixed로 처리)
  return -1;
};

// 선택 영역 내 Mixed 마커
const SELECTION_MIXED = '__SELECTION_MIXED__';

export const FontSizeControls: React.FC<{
  fontSize: number;
  itemId: string;
  itemType: 'text' | 'captions';
  disableCharacterStyleEditing?: boolean;
}> = ({ fontSize, itemId, itemType, disableCharacterStyleEditing = false }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { selectionStyle, applyFontSize } = useTextSelection();

  const { isMixed: isMultiItemMixed, value: selectedFontSize } =
    useSelectionValues<TextItem | CaptionsItem, number>(
      itemId,
      getFontSizeFromTextItem,
    );

  // 선택 상태 (disableCharacterStyleEditing이 true면 항상 false)
  const effectiveHasSelection = disableCharacterStyleEditing
    ? false
    : selectionStyle.hasSelection;

  // 최종 Mixed 여부 결정
  const isMixed = effectiveHasSelection
    ? selectionStyle.fontSize === SELECTION_MIXED
    : isMultiItemMixed || selectedFontSize === -1;

  // 표시할 fontSize 결정
  const displayFontSize = effectiveHasSelection
    ? selectionStyle.fontSize === SELECTION_MIXED
      ? fontSize
      : selectionStyle.fontSize
        ? parseInt(selectionStyle.fontSize, 10)
        : fontSize
    : fontSize;

  const onFontSizeChange: NumberControlUpdateHandler = useCallback(
    ({ num, commitToUndoStack }) => {
      const validFontSize = Math.max(
        1,
        Math.min(500, isNaN(num) ? DEFAULT_FONT_SIZE : num),
      );

      // 텍스트 선택 상태면 선택 영역에만 적용
      if (effectiveHasSelection) {
        applyFontSize(validFontSize);
        return;
      }

      // 전체 적용
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type === 'text') {
              return editAndRelayoutText(i, () => {
                if (
                  i.fontSize === validFontSize &&
                  !i.segments?.some((s) => s.fontSize)
                ) {
                  return i;
                }

                // segments에서 fontSize 속성 제거
                let newSegments = i.segments?.map((seg) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { fontSize: _fontSize, ...rest } = seg;
                  return rest;
                });
                // 모든 segment가 빈 객체(text만 있는)면 segments 제거
                const hasStyledSegments = newSegments?.some(
                  (s) =>
                    s.color ||
                    s.fontFamily ||
                    s.fontWeight ||
                    s.fontVariant ||
                    s.opacity !== undefined,
                );
                if (!hasStyledSegments) {
                  newSegments = undefined;
                }

                return {
                  ...i,
                  fontSize: validFontSize,
                  segments: newSegments,
                };
              });
            }

            if (i.type === 'captions') {
              if (i.fontSize === validFontSize) {
                return i;
              }

              const newItem = {
                ...i,
                fontSize: validFontSize,
              };

              return relayoutCaptions(newItem);
            }

            throw new Error(
              'Font size can only be changed for text and captions items',
            );
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, applyFontSize],
  );

  // +/- 버튼 디바운스 타이머
  const commitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSizeRef = useRef<number | null>(null);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
    };
  }, []);

  // 디바운스된 commit 함수
  const debouncedCommit = useCallback(
    (newSize: number) => {
      // 이전 타이머 취소
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }

      // character-level: 즉시 적용 (Lexical 자체 히스토리 사용)
      if (effectiveHasSelection) {
        applyFontSize(newSize);
        return;
      }

      // box-level: 즉시 적용 (undo stack에 저장 안 함)
      onFontSizeChange({ num: newSize, commitToUndoStack: false });
      pendingSizeRef.current = newSize;

      // 500ms 후에 undo stack에 저장
      commitTimeoutRef.current = setTimeout(() => {
        if (pendingSizeRef.current !== null) {
          onFontSizeChange({
            num: pendingSizeRef.current,
            commitToUndoStack: true,
          });
          pendingSizeRef.current = null;
        }
      }, 500);
    },
    [applyFontSize, onFontSizeChange],
  );

  // +/- 버튼 핸들러
  const handleIncrement = useCallback(() => {
    const newSize = Math.min(500, displayFontSize + 1);
    debouncedCommit(newSize);
  }, [displayFontSize, debouncedCommit]);

  const handleDecrement = useCallback(() => {
    const newSize = Math.max(1, displayFontSize - 1);
    debouncedCommit(newSize);
  }, [displayFontSize, debouncedCommit]);

  return (
    <div className="flex-1">
      <ValueInputField
        isUnit="px"
        isShowLabel={true}
        label={t('Editor.inspector.text.font-size')}
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <FormatSize className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onFontSizeChange}
        value={fontSize}
        min={1}
        max={500}
        step={1}
        accessibilityLabel="Font size"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};
